import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  createTheme,
  ThemeProvider,
  TextField,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form, useField } from "formik";
import * as Yup from "yup";
import theme from "../../styles/theme/theme";
import {
  clearBuyerMessages,
  verifyRoleOtp,
} from "../../features/user/userSlice";
import { useDispatch, useSelector } from "react-redux";
import { setCredentials } from "../../features/auth/authSlice";
import { useNavigate } from "react-router-dom";
import apiClient, { setAuthToken } from "../../services/apiClient";

const customTheme = createTheme({
  palette: {
    scraptradeYellow: {
      main: "#F4C542",
      dark: "#E3AE27",
    },
    scraptradeDark: {
      main: "#2D2D2D",
    },
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "8px",
          paddingTop: "10px",
          paddingBottom: "10px",
          fontWeight: 600,
          boxShadow: "none",
        },
      },
    },
  },
});

//  OTP Input Field Component
const OtpInput = ({ name, length, ...props }) => {
  const [field, meta, helpers] = useField(name);
  const [otpValues, setOtpValues] = useState(new Array(length).fill(""));
  const inputRefs = useRef([]);

  useEffect(() => {
    helpers.setValue(otpValues.join(""));
  }, [otpValues, helpers]);

  const handleChange = (index, event) => {
    const { value } = event.target;
    if (/[^0-9]/.test(value) && value !== "") return;

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value.slice(-1);
    setOtpValues(newOtpValues);

    if (value && index < length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  //  Allow full OTP paste (handles "123456")
  const handlePaste = (event) => {
    event.preventDefault();
    const pasteData = event.clipboardData.getData("text").trim();
    if (!/^\d+$/.test(pasteData)) return;

    const digits = pasteData.slice(0, length).split("");
    const newOtpValues = new Array(length).fill("");
    for (let i = 0; i < digits.length; i++) {
      newOtpValues[i] = digits[i];
    }

    setOtpValues(newOtpValues);

    // Focus the next empty input after paste
    const nextEmptyIndex = digits.length < length ? digits.length : length - 1;
    inputRefs.current[nextEmptyIndex]?.focus();
  };

  const errorText = meta.error && meta.touched ? meta.error : "";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        mt: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "center",
          mb: 1,
        }}
      >
        {otpValues.map((value, index) => (
          <TextField
            key={index}
            value={value}
            onChange={(e) => handleChange(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste} //  Paste listener here
            inputRef={(el) => (inputRefs.current[index] = el)}
            variant="outlined"
            suggestion="off"
            sx={{
              width: 48,
              "& .MuiOutlinedInput-root": {
                "& input": { textAlign: "center" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: customTheme.palette.scraptradeYellow.main,
                },
              },
              ...(value && {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: customTheme.palette.scraptradeYellow.main,
                  borderWidth: "2px",
                },
              }),
            }}
            inputProps={{
              maxLength: 1,
              style: { textAlign: "center", fontWeight: 600, fontSize: "18px" },
            }}
            error={!!errorText}
          />
        ))}
      </Box>
      {errorText && <FormHelperText error>{errorText}</FormHelperText>}
    </Box>
  );
};

//  Main Modal Component
const VerifyOtpModal = ({ show, onClose, onSuccess, role }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token, role: currentRole } = useSelector((state) => state.auth);
  const {
    verifyRoleOtpLoading,
    verifyRoleOtpError,
    verifyRoleOtpSuccess,
    roleSwitch,
  } = useSelector((state) => state.user);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (show) {
      dispatch(clearBuyerMessages());
      setError("");
      setSuccess("");
    }
  }, [show, dispatch]);

  const handleOtpSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = { role, otp: values.otp };
      const res = await dispatch(verifyRoleOtp(payload)).unwrap();
      console.log("verifyRoleOtp resolved:", res);

      if (res?.token && res?.role) {
        // Step 1: Immediately update axios headers
        setAuthToken(res.token);
        localStorage.setItem("access_token", res.token);

        // Step 2: Confirm axios actually has it
        if (
          apiClient.defaults.headers.common["Authorization"]?.includes(
            res.token
          )
        ) {
          console.log("Token set in axios headers");
        } else {
          console.warn("Token header mismatch, retrying...");
          await new Promise((resolve) => setTimeout(resolve, 200));
          setAuthToken(res.token);
        }

        // Step 3: Update Redux store
        dispatch(
          setCredentials({
            token: res.token,
            role: res.role,
            user: res.user || null,
          })
        );

        // Step 4: Navigate safely
        setSuccess(res.message || "Role switched successfully.");
        setTimeout(() => {
          const currentToken = localStorage.getItem("access_token");
          if (currentToken && res.role) {
            if (res.role === "seller")
              navigate("/seller/dashboard", { replace: true });
            else navigate("/dashboard", { replace: true });
          } else {
            console.warn("Delaying navigation, token not yet set");
          }
        }, 300);
      } else {
        setError(res?.message || "OTP verification failed.");
      }
    } catch (err) {
      console.error("verifyRoleOtp error:", err);
      setError("Something went wrong while verifying OTP.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Modal
        open={show}
        onClose={onClose}
        aria-labelledby="verify-otp-modal"
        aria-describedby="verify-otp-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: "16px",
            width: "90%",
            maxWidth: 420,
            p: 4,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              Verify OTP
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Info Message */}
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              textAlign: "center",
              mb: 3,
              px: 1,
            }}
          >
            You are about to switch your account role to{" "}
            <Typography
              component="span"
              fontWeight={600}
              color={customTheme.palette.scraptradeYellow.main}
            >
              {role === "seller" ? "Seller" : "Buyer"}
            </Typography>
            . Please enter the 6-digit OTP sent to your registered email to
            confirm this action.
          </Typography>

          {/* API Message Handling via Redux */}
          {verifyRoleOtpError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {verifyRoleOtpError?.message || "OTP verification failed"}
            </Alert>
          )}

          {verifyRoleOtpSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {verifyRoleOtpSuccess}
            </Alert>
          )}

          {/* OTP Form */}
          <Formik
            initialValues={{ otp: "" }}
            validationSchema={Yup.object({
              otp: Yup.string()
                .length(6, "Enter 6-digit OTP")
                .required("OTP is required"),
            })}
            onSubmit={handleOtpSubmit}
          >
            {({ values, isSubmitting }) => {
              // console.log("Formik values:", values);
              return (
                <Form>
                  <OtpInput name="otp" length={6} />

                  {/* Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 1,
                      mt: 3,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={onClose}
                      disabled={isSubmitting}
                      sx={{
                        borderColor: customTheme.palette.scraptradeYellow.main,
                        color: customTheme.palette.scraptradeYellow.main,
                        "&:hover": {
                          borderColor:
                            customTheme.palette.scraptradeYellow.dark,
                          backgroundColor:
                            customTheme.palette.scraptradeYellow.main + "1a",
                        },
                      }}
                    >
                      Cancel
                    </Button>

                    <Button
                      type="submit"
                      variant="contained"
                      disabled={values.otp.length !== 6 || isSubmitting}
                      sx={{
                        backgroundColor:
                          customTheme.palette.scraptradeYellow.main,
                        color: "#2D3A27",
                        "&:hover": {
                          backgroundColor:
                            customTheme.palette.scraptradeYellow.dark,
                        },
                      }}
                    >
                      {verifyRoleOtpLoading || isSubmitting ? (
                        <CircularProgress size={22} color="inherit" />
                      ) : (
                        "Verify"
                      )}
                    </Button>
                  </Box>
                </Form>
              );
            }}
          </Formik>
        </Box>
      </Modal>
    </ThemeProvider>
  );
};

export default VerifyOtpModal;
